import React, { useEffect, useState } from "react";
import { Modal, Row, Col, Spinner } from "react-bootstrap";
import styled from "styled-components";
import CommonTable from "components/CommonTable";
import {
	CustomControl,
	CustomButton,
	CustomSelect,
	CustomNumberInput,
} from "components/CustomForm";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "react-query";
import { createProduct, updateProduct, getAllProducts } from "network/apis/ProductApi";
import { getUnits, getIngredients } from "network/apis/CommonApi";
import { isBlank, getErrorMessage } from "helpers/strings";
import { DateObject } from "react-multi-date-picker";
import { TiDocumentDelete } from "react-icons/ti";

const cols = ["حذف", "واحد", "مقدار", "نوع نهاده/ترکیب", "ردیف"];

const ModalHolder = styled(Modal)`
  font-family: "YEKAN-BAKH-MEDIUM";
`;

const Header = styled.div`
  font-size: 18pt;
  width: 100%;
  text-align: center;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
`;

const CloseButton = styled.div`
  background-color: #424250;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  position: absolute;
  top: 12px;
  right: 10px;
`;

const EmptyList = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 7rem;
  text-align: center;
  color: #616161;
  font-size: 14pt;
`;

const AddProductModal = ({
	isOpen,
	onClose,
	onCreateSuccess,
	editData,
	...rest
}) => {
	const isEditMode = () => {
		if (editData && editData != null) {
			return true;
		}

		else return false;
	};

	const [loading, setLoading] = useState(false);
	/// list of products currently in table
	const [productsList, setProductsList] = useState([]);
	/// list of products added to currnet list (only in edit mode)
	const [addedProducts, setAddedProducts] = useState([]);
	/// list of products removed from currnet list (only in edit mode)
	const [removedProducts, setRemovedProducts] = useState([]);
	const [selectedInput, setSelectedInput] = useState();
	const [amount, setAmount] = useState();
	const [selectedUnit, setSelectedUnit] = useState();
	const [description, setDescription] = useState();
	const [refreshKey, setRefreshKey] = useState(0);

	useEffect(() => {
		if (editData) {
			const list = editData.items.map((item) => (
				{
					item_id: item.item_id,
					input: { label: item.food_item.name, value: item.food_item.item_id },
					amount: item.amount,
					unit: { label: item.unit.name, value: item.unit.unit_id },
					/// all product objects in edit mode have this flag, so that in removal
					/// we can track which objects should be sent to `removed_items` in server api
					isNew: true,
				}
			));
			setProductsList(list);
			setDescription(editData.description);
		}
	}, [editData]);

	const { data: allInputs } = useQuery(
		["get-all-inputs"],
		() => getAllProducts(),
		{
			refetchOnWindowFocus: false,
		}
	);

	const { data: unitsData } = useQuery(["get-units"], () => getUnits(), {
		refetchOnWindowFocus: false,
	});

	const productMutation = useMutation(
		isEditMode() ? updateProduct : createProduct,
		{
			onSuccess: (res) => {
				if (res && res.data) {
					toast(`با موفقیت ${isEditMode() ? "ویرایش" : "ایجاد"} گردید`, {
						type: "success",
						position: "bottom-right",
					});
					onCreateSuccess?.();
					onClose();
				}
			},
			onError: (err) => {
				toast(getErrorMessage(err), {
					type: "error",
					position: "bottom-right",
				});
			},
			onSettled: () => {
				setLoading(false);
			},
		}
	);

	const getInputOptions = () =>
		allInputs
			? allInputs.map((input, index) => {
				return { value: input?.item_id, label: input?.name };
			})
			: [];

	const getUnitOptions = () =>
		unitsData?.data
			? unitsData.data.map((unit, index) => {
				return { value: unit?.unit_id, label: unit?.name };
			})
			: [];

	const addToList = () => {
		let error;
		if (!selectedInput) {
			error = "مشخص کردن نوع ترکیب ضروری است";
		}
		if (isBlank(amount)) {
			error = "مشخص کردن مقدار ضروری است";
		}
		if (!selectedUnit) {
			error = "مشخص کردن واحد ضروری است";
		}

		if (error) {
			toast(error, {
				type: "error",
				position: "bottom-right",
			});
			return;
		}

		const newItem = {
			input: selectedInput,
			amount: amount,
			unit: selectedUnit,
		};

		const hasSameItem = productsList.filter(
			(item) => item.input.value === newItem.input.value
		).length > 0;

		if (!hasSameItem) {
			//add item to productsList, so that it is shown in table and ready to be sent for server
			setProductsList((prev) => [...prev, newItem]);
			//also add item to addedProducts list, this is only used if in edit mode
			setAddedProducts((prev) => [...prev, newItem]);
		}

		resetForm();
	};

	const resetForm = () => {
		setSelectedInput(null);
		setAmount(null);
		setSelectedUnit(null);
		setRefreshKey((prev) => prev + 1);
	};

	const deleteFromList = (index) => {
		// object of item which is requested to be removed
		const itemToBeRemoved = { ...productsList[index] };

		// index of itemToBeRemoved in addedProducts list (this is used only in edit mode)
		const indexOfItemInAddedProducts = addedProducts.findIndex(
			(addedItem) => addedItem.input?.value === itemToBeRemoved?.input?.value
		);
		// if itemToBeRemoved was already in list, the add it also to removedProducts, so
		// we will send it for server to be removed, ELSE the item should be added just now (while edit)
		// so we will remove it from addedProducts list
		if (itemToBeRemoved?.isNew) {
			//add itemToBeRemoved to removedProducts
			setRemovedProducts((prev) => [itemToBeRemoved, ...prev]);
		} else {
			//remove itemToBeRemoved from addedProducts
			const newAddedProducts = [...addedProducts];
			newAddedProducts.splice(indexOfItemInAddedProducts, 1);
			setAddedProducts(newAddedProducts);
		}

		// regardless of edit mode, remove item from main list of products
		const newList = [...productsList];
		newList.splice(index, 1);
		setProductsList(newList);
	};

	const sendList = () => {
		let error;
		if (productsList.length === 0) {
			error = "هیچ آیتمی ثبت نشده است";
		}
		if (isBlank(description)) {
			error = "مشخص کردن توضیحات ضروری است";
		}

		if (error) {
			toast(error, {
				type: "error",
				position: "bottom-right",
			});
			return;
		}

		setLoading(true);
		if (isEditMode()) {
			const editPayload = {
				id: editData.conc_id,
				removedItems: removedProducts,
				addedItems: addedProducts,
				description,
			};

			productMutation.mutate(editPayload);
		} else {
			productMutation.mutate({ productsList, description });
		}
	};

	const onInputChange = (newVal) => {
		setSelectedInput(newVal);

		const targetInput = allInputs?.find((i) => i.item_id == newVal.value);
		if (targetInput) {
			const defaultUnit = {
				value: targetInput.default_unit?.unit_id,
				label: targetInput.default_unit?.name
			};
			setSelectedUnit(defaultUnit);
		}
	};

	return (
		<ModalHolder
			show={isOpen}
			size={"lg"}
			centered
			animation={false}
			onHide={onClose}
			{...rest}
		>
			<Modal.Body className="px-5">
				<CloseButton onClick={onClose}>
					<MdClose color="#fff" />
				</CloseButton>
				<Header>
					<span>ثبت تولید خوراک دام (کنسانتره)</span>
				</Header>
				<Row className="d-flex justify-content-between me-3 mb-4">
					<Col md={3} className="d-flex justify-content-end">
						<CustomButton onClick={addToList}>افزودن به لیست</CustomButton>
					</Col>
					<Col md={3}>
						<CustomSelect
							placeholder="واحد"
							options={getUnitOptions()}
							onChange={setSelectedUnit}
							value={selectedUnit}
							radius="14"
						/>
					</Col>
					<Col md={3}>
						<CustomNumberInput
							key={refreshKey}
							placeholder="مقدار"
							radius="14"
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
						/>
					</Col>
					<Col md={3}>
						<CustomSelect
							placeholder="نوع ترکیب"
							options={getInputOptions()}
							radius="14"
							value={selectedInput}
							onChange={onInputChange}
						/>
					</Col>
				</Row>
				<div>
					{productsList.length > 0 && (
						<CommonTable cols={cols}>
							{productsList.map((item, index) => (
								<tr key={index}>
									<td>
										<TiDocumentDelete onClick={() => deleteFromList(index)} />
									</td>
									<td>{item.unit?.label}</td>
									<td>{item.amount}</td>
									<td>{item.input?.label}</td>
									<td>{index + 1}</td>
								</tr>
							))}
						</CommonTable>
					)}
					{productsList.length === 0 && (
						<EmptyList>هیج آیتمی وجود ندارد</EmptyList>
					)}
				</div>
				<div>
					<CustomControl
						as="textarea"
						className="w-100 mt-3"
						placeholder="توضیحات"
						radius="14"
						textAlign="end"
						rows={4}
						paddings="1rem"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
					/>
				</div>
				<div className="d-flex justify-content-center mt-4 mb-3">
					<CustomButton fontSize={16} className="w-25" onClick={sendList} disabled={loading}>
						{loading ? (
							<Spinner animation="border" role="status" size="sm" />
						) : (
							<span>{isEditMode() ? "ویرایش" : "ثبت"}</span>
						)}
					</CustomButton>
					<CustomButton
						className="ms-3 w-25"
						color="#7300FF"
						fontSize={16}
						onClick={onClose}
					>
						انصراف
					</CustomButton>
				</div>
			</Modal.Body>
		</ModalHolder>
	);
};

export default AddProductModal;
